import { API_BASE_URL } from '../config';
import { authFetch } from './auth';
import type {
  ResumeOptimizeRequest,
  ResumeOptimizeResponse,
  TargetLevel,
} from '../types/resumeOptimize';

// 支持的文件类型
const ALLOWED_FILE_TYPES = [
  'text/plain',
  'text/markdown',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
];

const ALLOWED_EXTENSIONS = ['.txt', '.md', '.docx', '.pdf'];

// 最大文件大小 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// 验证文件
export function validateResumeFile(file: File): string | null {
  // 检查文件类型
  const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) =>
    file.name.toLowerCase().endsWith(ext),
  );

  if (!ALLOWED_FILE_TYPES.includes(file.type) && !hasValidExtension) {
    return '只支持 TXT、MD、DOCX、PDF 格式的文件';
  }

  // 检查文件大小
  if (file.size > MAX_FILE_SIZE) {
    return '文件大小不能超过 10MB';
  }

  return null;
}

// 简历优化 API
export async function optimizeResume(
  params: ResumeOptimizeRequest,
): Promise<ResumeOptimizeResponse> {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('enableJDMatch', String(params.enableJDMatch));

  // PDF渲染的图片（用于图片型PDF识别）
  if (params.picFile) {
    formData.append('pic', params.picFile);
  }

  // 定向模式参数
  if (params.jobName) {
    formData.append('jobName', params.jobName);
  }
  if (params.jobLevel) {
    formData.append('jobLevel', params.jobLevel);
  }
  if (params.jobDescription) {
    formData.append('jobDescription', params.jobDescription);
  }

  const response = await authFetch(`${API_BASE_URL}/api/resume/optimize`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || '简历优化失败，请稍后重试');
  }

  return response.json();
}

// 获取目标级别显示文本
export function getTargetLevelText(level: TargetLevel): string {
  const levelMap: Record<TargetLevel, string> = {
    junior: '初级',
    mid: '中级',
    senior: '高级',
  };
  return levelMap[level];
}

// 获取优先级显示文本和颜色
export function getPriorityInfo(
  priority: 'critical' | 'high' | 'medium' | 'low',
) {
  const priorityMap = {
    critical: {
      text: '极其重要',
      color: 'text-red-600',
      bgColor: 'bg-red-600/40',
    },
    high: { text: '高优先级', color: 'text-red-400', bgColor: 'bg-red-500/20' },
    medium: {
      text: '中优先级',
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-500/20',
    },
    low: {
      text: '低优先级',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/20',
    },
  };
  return priorityMap[priority];
}

// 获取竞争力显示文本（使用正则匹配，只要包含就匹配）
export function getCompetitivenessText(level: string): string {
  const levelLower = level.toLowerCase();
  if (/high/.test(levelLower)) return '高';
  if (/medium/.test(levelLower)) return '中';
  if (/low/.test(levelLower) && !/extremely/.test(levelLower)) return '低';
  if (/none|unknown/.test(levelLower)) return '无';
  return level;
}
